import React, {FC} from 'react';
import styled from 'styled-components';

import {atIndex} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {
  ButtonVariantsEnum,
  findButtonColors,
} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {DropdownItemContactAvatar} from '../dropdowns/dropdownItemContactAvatar';
import {ContactAvatar} from './contactAvatar';

/*
 * Props.
 */

export interface DropdownItemMultipleContactAvatarProps {
  contacts: ReadonlyArray<VisualContactModel>;
  size: VisualSizesEnum | number;
}

/*
 * Styles.
 */

const StyledAvatarsDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
`;

const StyledSecondContactDiv = styled.div`
  margin-top: 10px;
  margin-left: -9px;
`;

const StyledFirstContactDiv = styled.div`
  margin-bottom: 10px;
`;

/*
 * Component.
 */

export const DropdownItemMultipleContactAvatar: FC<DropdownItemMultipleContactAvatarProps> = (props) => {
  const {contacts, size} = props;
  const theme = useUserTheme();

  const firstContact = atIndex(contacts, 0);
  const secondContact = atIndex(contacts, 1);

  if (!firstContact) {
    return null;
  }

  if (!secondContact) {
    return <DropdownItemContactAvatar contact={firstContact} />;
  }

  return (
    <StyledAvatarsDiv>
      <StyledSecondContactDiv>{renderContactAvatar(secondContact, size, theme)}</StyledSecondContactDiv>
      <StyledFirstContactDiv>{renderContactAvatar(firstContact, size, theme)}</StyledFirstContactDiv>
    </StyledAvatarsDiv>
  );
};

function renderContactAvatar(contact: VisualContactModel, size: VisualSizesEnum | number, theme: UserTheme) {
  const buttonColors = findButtonColors(theme);

  const avatarBorderColor = buttonColors[ButtonVariantsEnum.SECONDARY].background(PaletteColorsEnum.GREY);
  const avatarBorderHoverColor = buttonColors[ButtonVariantsEnum.SECONDARY].backgroundHover(
    PaletteColorsEnum.GREY,
  );

  if (contact.teammate) {
    return (
      <TeammateAvatar
        borderColor={avatarBorderColor}
        borderHoverColor={avatarBorderHoverColor}
        size={size}
        teammate={contact.teammate}
      />
    );
  }

  return (
    <ContactAvatar
      borderColor={avatarBorderColor}
      borderHoverColor={avatarBorderHoverColor}
      visualContact={contact}
      size={size}
    />
  );
}
